import { useId, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input, Select } from '../../../../components/ui';
import type { FieldControlProps } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { loiTheoTruong } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { usePhanLoai, useSuaChiTiet, useThemChiTiet } from '../../hooks/useCauHinhLoi';
import {
  DAI_EXCEPTION,
  GIOI_HAN,
  chiTietThanhForm,
  chiTietTrong,
  chuanHoaChiTiet,
  kiemChiTiet,
  luaChonPhanLoai,
} from '../../nghiep-vu/loi';
import type { LoiChiTiet } from '../../nghiep-vu/loi';
import type { ChiTietLoi, ThanChiTietLoi } from '../../types';
import { DongBatTat } from '../chung';

/**
 * Thêm hoặc sửa một dòng chi tiết (Bảng 2) của code: exception, đoạn thông điệp tuỳ chọn, phân loại. Exception đã
 * thuộc một code khác (`409 RPA_ERROR_DETAIL_EXISTS`) báo ngay dưới ô exception.
 */
export function HopChiTiet({ code, goc, onClose }: { code: string; goc: ChiTietLoi | null; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const them = useThemChiTiet();
  const sua = useSuaChiTiet();
  const dang = goc ? sua : them;
  const [than, setThan] = useState<ThanChiTietLoi>(() => (goc ? chiTietThanhForm(goc) : chiTietTrong()));
  const [loi, setLoi] = useState<LoiChiTiet>({});

  const dat = <K extends keyof ThanChiTietLoi>(k: K, v: ThanChiTietLoi[K]) => {
    setThan((cu) => ({ ...cu, [k]: v }));
    dang.reset();
  };

  const truong = loiTheoTruong(dang.error);
  const loiTrung = dang.error?.code === 'RPA_ERROR_DETAIL_EXISTS' ? dichLoi(dang.error) : undefined;
  const loiException = loi.exception
    ? t(loi.exception === 'batBuoc' ? 'rpaLoi.formCt.loiBatBuoc' : 'rpaLoi.formCt.loiSaiDang')
    : (loiTrung ?? truong['/exception']);
  const loiChung = dang.error && !loiTrung && Object.keys(truong).length === 0 ? dichLoi(dang.error) : null;

  const gui = () => {
    const thay = kiemChiTiet(than);
    setLoi(thay);
    if (Object.keys(thay).length > 0 || dang.isPending) return;
    const du = chuanHoaChiTiet(than);
    const xong = () => {
      toast.success(t(goc ? 'rpaLoi.formCt.daLuu' : 'rpaLoi.formCt.daThem', { exception: du.exception }));
      onClose();
    };
    if (goc) sua.mutate({ id: goc.id, than: du }, { onSuccess: xong });
    else them.mutate({ code, than: du }, { onSuccess: xong });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={620}
      destroyOnHidden
      title={goc ? t('rpaLoi.formCt.suaTieuDe') : t('rpaLoi.formCt.themTieuDe', { ma: code })}
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending}>
              {dang.isPending ? t('chung.dangLuu') : t(goc ? 'rpaLoi.formCt.luu' : 'rpaLoi.formCt.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field label={t('rpaLoi.formCt.exception')} required hint={t('rpaLoi.formCt.exceptionGoiY')} error={loiException}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              className="font-mono"
              maxLength={DAI_EXCEPTION}
              value={than.exception}
              placeholder={t('rpaLoi.formCt.exceptionMau')}
              onChange={(e) => dat('exception', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('rpaLoi.formCt.thongDiep')} hint={t('rpaLoi.formCt.thongDiepGoiY')} error={truong['/messageContains']}>
          {(p) => (
            <Input {...p} maxLength={GIOI_HAN.thongDiep} value={than.messageContains} onChange={(e) => dat('messageContains', e.target.value)} />
          )}
        </Field>
        <Field label={t('rpaLoi.formCt.phanLoai')} hint={t('rpaLoi.formCt.phanLoaiGoiY')} error={truong['/classification']}>
          {(p) => <ChonPhanLoai p={p} giaTri={than.classification} onDoi={(v) => dat('classification', v)} />}
        </Field>
        <DongBatTat nhan={t('rpaLoi.formCt.bat')} goiY={t('rpaLoi.formCt.batGoiY')} bat={than.enabled} onDoi={(v) => dat('enabled', v)} />
      </form>
    </Dialog>
  );
}

/** Chọn phân loại đã dùng ở các dòng chi tiết khác; gõ tên chưa có thì có dòng "Thêm phân loại …" ở đầu danh sách. */
function ChonPhanLoai({ p, giaTri, onDoi }: { p: FieldControlProps; giaTri: string; onDoi: (v: string) => void }) {
  const { t } = useT();
  const [dangGo, setDangGo] = useState('');
  const biet = usePhanLoai().data;
  const luaChon = useMemo(
    () => luaChonPhanLoai(giaTri ? [...(biet ?? []), giaTri] : (biet ?? []), dangGo.slice(0, GIOI_HAN.phanLoai)),
    [biet, giaTri, dangGo],
  );

  return (
    <Select<string>
      {...p}
      showSearch={{
        onSearch: setDangGo,
        filterOption: (nhap, o) =>
          Boolean((o as { moi?: boolean } | undefined)?.moi) ||
          String(o?.value ?? '').toLowerCase().includes(nhap.trim().toLowerCase()),
      }}
      allowClear
      className="w-full"
      value={giaTri || undefined}
      placeholder={t('rpaLoi.formCt.phanLoaiChon')}
      notFoundContent={t('rpaLoi.formCt.phanLoaiRong')}
      options={luaChon.map((o) => ({ value: o.value, label: o.value, moi: o.moi }))}
      optionRender={(o) =>
        (o.data as { moi?: boolean }).moi ? (
          <span className="inline-flex items-center gap-2 text-primary-700">
            <PlusOutlined aria-hidden />
            {t('rpaLoi.formCt.phanLoaiThem', { ten: String(o.value) })}
          </span>
        ) : (
          <span className="truncate">{String(o.label)}</span>
        )
      }
      onBlur={() => setDangGo('')}
      onChange={(v) => {
        setDangGo('');
        onDoi(v ?? '');
      }}
    />
  );
}
